import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { Dialog } from '@/components/AppDialog'
import { useEffect, useState } from 'react'
import { useGrantPortalAccess, usePortalAccess, useRevokePortalAccess } from '@/api/hooks'
import { ErrorBanner, formatDateTime } from '@/components/common'

/**
 * Giving a customer a way in to see their own file.
 *
 * <p>The password is shown once, here, for whoever is on the telephone to read
 * out. It is stored hashed and cannot be retrieved afterwards — a reset is the
 * only way back, which is what stops a stored password being read by anybody
 * who reaches this screen later.</p>
 */
export function PortalAccessDialog({
  customerId,
  customerName,
  onClose,
}: {
  customerId: string | null
  customerName?: string
  onClose: () => void
}) {
  const { data: access, error } = usePortalAccess(customerId ?? undefined)
  const grant = useGrantPortalAccess()
  const revoke = useRevokePortalAccess()
  const [password, setPassword] = useState('')
  const [issued, setIssued] = useState<{ username: string; password: string } | null>(null)

  useEffect(() => {
    setPassword(suggest())
    setIssued(null)
  }, [customerId])

  const open = Boolean(customerId)
  const live = access?.granted && access.active

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Portal access for {customerName}</DialogTitle>
      <DialogContent>
        <ErrorBanner error={error ?? grant.error ?? revoke.error} />

        {issued ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Give them these. This is the only time the password is shown.
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', mt: 1 }}>
              Sign in at /my/login
              <br />
              Mobile: {issued.username}
              <br />
              Password: {issued.password}
            </Typography>
          </Alert>
        ) : null}

        <Typography variant="body2" color="text.secondary" gutterBottom>
          {live
            ? `They can sign in with ${access?.username}. ${
                access?.lastLoginAt
                  ? `Last signed in ${formatDateTime(access.lastLoginAt)}.`
                  : 'They have not signed in yet.'
              }`
            : 'They cannot sign in yet. Set a password and pass it on.'}
        </Typography>

        {access?.locked ? (
          <Alert severity="warning" sx={{ my: 1 }}>
            Locked after too many wrong passwords. Setting a new one unlocks it.
          </Alert>
        ) : null}

        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 2 }}>
          <TextField
            fullWidth
            size="small"
            label={live ? 'New password' : 'Password'}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            helperText="At least 8 characters. They are asked to change it once they are in."
            inputProps={{ 'aria-label': 'Portal password' }}
          />
          <Button size="small" onClick={() => setPassword(suggest())} sx={{ mt: 0.5 }}>
            Suggest
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions>
        {live ? (
          <Button
            color="error"
            disabled={revoke.isPending}
            onClick={() => customerId && revoke.mutate(customerId, { onSuccess: onClose })}
          >
            Withdraw access
          </Button>
        ) : null}
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          disabled={password.trim().length < 8 || grant.isPending}
          onClick={() =>
            customerId &&
            grant.mutate(
              { customerId, password: password.trim() },
              { onSuccess: (result) => setIssued(result) },
            )
          }
        >
          {live ? 'Reset password' : 'Give access'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

/** Something readable over the telephone: no l/1/O/0 to mishear. */
function suggest() {
  const letters = 'abcdefghjkmnpqrstuvwxyz'
  const digits = '23456789'
  const word = Array.from({ length: 6 }, () =>
    letters[Math.floor(Math.random() * letters.length)],
  ).join('')
  const number = Array.from({ length: 3 }, () =>
    digits[Math.floor(Math.random() * digits.length)],
  ).join('')
  return `${word}${number}`
}
